"use client"

import {
  BarChart3,
  BookOpen,
  FolderGit2,
  Hammer,
  LogIn,
  LogOut,
} from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { usePathname } from "next/navigation"
import React, { useState } from "react"

import NavLink from "@packages/web/components/NavLink"
import Spinner from "@packages/web/components/Spinner"

import SignLink from "./SignLink"
import useGetAuthentication from "@packages/base/hooks/use-get-authentication"

const menuItemClass =
  "border-none block w-full text-left px-4 py-3 hover:bg-muted transition-colors text-foreground disabled:opacity-70 !font-normal !bg-transparent cursor-pointer hover:!scale-100"
const currentPageItemClass =
  "block w-full text-left px-4 py-3 cursor-default text-primary font-semibold bg-muted [&_svg]:text-primary-base"

type AccountRole = "user" | "editor"

type MenuItem = {
  Icon: React.ComponentType<{ className?: string }>
  label: string
  roleOnly?: AccountRole
  to: string
}

const editorNavItems: MenuItem[] = [
  { Icon: BarChart3, label: "Analytics", roleOnly: "editor", to: "/dashboard/analytics" },
  { Icon: Hammer, label: "Crafts", roleOnly: "editor", to: "/dashboard/crafts" },
  { Icon: BookOpen, label: "Guidelines", roleOnly: "editor", to: "/dashboard/guidelines" },
  { Icon: FolderGit2, label: "Worktrees", roleOnly: "editor", to: "/dashboard/worktrees" },
]

export interface TasksNavConfig {
  wipTaskCount?: number
  onActivatePickMode?: () => void
  onOpenGlobalTaskAssistant?: () => void
}

function MenuNavItem({
  closeMenu,
  isCurrent,
  item,
}: {
  closeMenu: () => void
  isCurrent: boolean
  item: MenuItem
}) {
  const { Icon, label, to } = item
  const content = (iconClass: string) => (
    <div className="flex items-center gap-2.5 text-sm">
      <Icon className={`w-4 h-4 shrink-0 ${iconClass}`} />
      {label}
    </div>
  )

  if (isCurrent) {
    return (
      <div className={currentPageItemClass} aria-current="page">
        {content("text-primary-base")}
      </div>
    )
  }

  return (
    <NavLink to={to} size="lg" variant="secondary" className={menuItemClass} onClick={closeMenu}>
      {content("text-muted-foreground")}
    </NavLink>
  )
}

export default function NavMenu({ tasksConfig: _tasksConfig = {} }: { tasksConfig?: TasksNavConfig }) {
  const queryClient = useQueryClient()
  const pathname = usePathname()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { data: authentication, isSuccess: isAuthenticationSuccess } = useGetAuthentication({
    redirectIfNotAuthenticated: false,
  }, queryClient)
  const isCurrentPage = (to: string) => pathname === to || pathname.startsWith(`${to}/`)
  const visibleEditorNavItems = editorNavItems.filter((item) => {
    if (item.roleOnly === "editor") return Boolean(authentication?.isEditor)
    return true
  })
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)
  const closeMenu = () => setIsMenuOpen(false)

  if (!isAuthenticationSuccess || !authentication?.isAuthenticated) {
    return null
  }

  return (
    <div className={`relative flex items-center gap-1 ${isMenuOpen ? "z-1000" : ""}`}>
      <button
        onClick={toggleMenu}
        className="cursor-pointer flex flex-col justify-center items-center w-8 h-8 space-y-1.5 focus:outline-none text-foreground/70 hover:text-foreground transition-colors"
        aria-label="Menu"
      >
        <span
          className={`block w-[20px] h-px bg-current transition-all duration-300 ${isMenuOpen ? "rotate-45 translate-y-[6px] w-[28px]" : ""}`}
        ></span>
        <span className={`block w-[20px] h-px bg-current transition-all duration-300 ${isMenuOpen ? "opacity-0" : ""}`}></span>
        <span
          className={`block w-[20px] h-px bg-current transition-all duration-300 ${isMenuOpen ? "-rotate-45 -translate-y-[7px] w-[28px]" : ""}`}
        ></span>
      </button>

      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={closeMenu} />
          <nav className="absolute right-0 top-full mt-3 w-52 bg-card rounded-xl shadow-lg z-50 overflow-hidden py-1">
            {visibleEditorNavItems.length > 0 ? (
              <>
                {visibleEditorNavItems.map((item) => (
                  <MenuNavItem
                    key={item.to}
                    closeMenu={closeMenu}
                    isCurrent={isCurrentPage(item.to)}
                    item={item}
                  />
                ))}
              </>
            ) : null}
            <div className="my-1 border-t border-border/50 mx-2 mt-4" role="separator" />
            <SignLink
              className={menuItemClass}
              onSignoutSuccess={closeMenu}
              render={({ isAuthenticated, isPending }) => (
                <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
                  {isAuthenticated ? (
                    <>
                      {isPending ? (
                        <Spinner height={1} width={1} className="shrink-0 [&>div]:border-foreground" />
                      ) : (
                        <LogOut className="w-4 h-4 text-muted-foreground" />
                      )}
                      {isPending ? "Déconnexion..." : "Se déconnecter"}
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 text-muted-foreground" />
                      Se connecter
                    </>
                  )}
                </div>
              )}
            />
          </nav>
        </>
      )}
    </div>
  )
}
